'use client'

import { useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import NotificationBell from './NotificationBell'

export default function Navbar({ session, userPoints, categories = [] }: { session: any, userPoints: number, categories?: any[] }) {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const router = useRouter()

  const isAdmin = session?.user ? (session.user as any).role === 'ADMIN' : false

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      router.push(`/?search=${encodeURIComponent(searchQuery)}`)
    }
  }

  const userImage = session?.user?.image

  return (
    <>
      {/* Update line 23 in your Navbar component */}
      <header className="sticky top-0 z-50 bg-slate-950/90 backdrop-blur-md border-b border-slate-800/60 shadow-sm">
        {/* Changed max-w-7xl to max-w-[1600px] */}
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          {/* Left: Mobile Menu Trigger & Logo */}
          <div className="flex items-center space-x-3 shrink-0">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-2 rounded-lg bg-slate-900 text-slate-300 hover:text-white transition-colors border border-slate-800"
              aria-label="Open Mobile Menu"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
              </svg>
            </button>

            <Link href="/" className="flex items-center space-x-2.5 group">
              <div className="bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 p-1.5 rounded-lg flex items-center justify-center font-bold text-xs shadow-sm group-hover:bg-indigo-500/20 transition-colors">
                DY
              </div>
              <span className="text-lg font-bold tracking-tight text-slate-100 group-hover:text-white transition-colors">
                DevYield
              </span>
            </Link>
          </div>

          {/* Center: IDE-Style Command Palette Search */}
          <form onSubmit={handleSearch} className="flex items-center relative flex-1 max-w-xs sm:max-w-md mx-2 group">
            <span className="absolute left-3 text-slate-500 text-sm transition-colors group-focus-within:text-indigo-400">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            </span>
            <input 
              type="text" 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search discussions..." 
              className="w-full pl-9 pr-10 py-1.5 bg-slate-900/50 border border-slate-800 rounded-lg text-sm font-mono text-slate-200 placeholder:font-sans placeholder:text-slate-500 focus:outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/50 focus:bg-slate-900 transition-all shadow-inner"
            />
            <span className="hidden sm:flex absolute right-2 items-center justify-center px-1.5 py-0.5 text-[10px] font-mono font-bold text-slate-500 bg-slate-800/80 border border-slate-700 rounded transition-opacity group-focus-within:opacity-0">
              ⌘K
            </span>
          </form>

          {/* Right: Actions & Profile Dropdown */}
          <div className="flex items-center space-x-4 shrink-0">
            
            <Link 
              href="/store" 
              className="hidden md:flex items-center gap-1.5 text-[11px] font-bold text-slate-400 hover:text-amber-400 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
              </svg>
              Store
            </Link>

            {session ? (
              <div className="relative flex items-center gap-3">
                
                {/* CLI-Style Points */}
                <Link href="/store" className="hidden sm:flex items-center gap-1 text-[11px] font-mono font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-1 rounded-md hover:bg-emerald-500/20 transition-colors">
                  <span className="opacity-50">$</span>
                  {userPoints.toFixed(1)} pts
                </Link>
                
                <NotificationBell currentUserId={session?.user ? (session.user as any).id : null} />
                
                {/* Glowing Avatar Trigger */}
                <div className="relative group/nav-avatar">
                  <div className={`absolute inset-0 rounded-lg blur opacity-40 group-hover/nav-avatar:opacity-80 transition-opacity ${
                    isAdmin ? 'bg-rose-500' : 'bg-indigo-500'
                  }`} />
                  <button 
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className={`relative h-8 w-8 rounded-lg p-[2px] cursor-pointer flex items-center justify-center text-white font-bold text-xs shadow-md ${
                      isAdmin ? 'bg-gradient-to-br from-rose-500 to-rose-900' : 'bg-gradient-to-br from-slate-700 to-slate-900'
                    }`}
                  >
                    {userImage ? (
                      <img src={userImage} alt={session.user?.name || 'Avatar'} className="w-full h-full object-cover rounded-md bg-slate-950" />
                    ) : (
                      session.user?.name?.[0]?.toUpperCase() || 'U'
                    )}
                  </button>
                </div>

                {/* Dropdown Menu */}
                {dropdownOpen && (
                  <div className="absolute top-full right-0 mt-3 w-56 bg-slate-900/95 backdrop-blur-xl border border-slate-800 rounded-xl shadow-2xl py-2 z-50 text-sm overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-800/60 bg-slate-950/50">
                      <p className="font-bold text-slate-200 truncate">{session.user?.name}</p>
                      <p className="text-[11px] font-mono text-slate-500 truncate mt-0.5">{session.user?.email}</p>
                    </div>
                    
                    <div className="p-1.5">
                      <Link 
                        href={`/user/${(session.user as any).id}`} 
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-2.5 px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-colors text-xs font-medium"
                      >
                        <span className="opacity-70">👤</span> View Profile
                      </Link>
                      <Link 
                        href="/vault" 
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-2.5 px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-colors text-xs font-medium"
                      >
                        <span className="opacity-70">🔒</span> Script Vault
                      </Link>
                      <Link 
                        href="/settings" 
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-2.5 px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-colors text-xs font-medium"
                      >
                        <span className="opacity-70">⚙️</span> Settings
                      </Link>
                    </div>
                    
                    <div className="border-t border-slate-800/60 my-0.5"></div>
                    
                    <div className="p-1.5">
                      <Link 
                        href="/store" 
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-2.5 px-3 py-2 text-amber-400 hover:bg-amber-500/10 rounded-lg transition-colors text-xs font-bold"
                      >
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
                        Upgrade to Pro
                      </Link>
                    </div>

                    <div className="border-t border-slate-800/60 my-0.5"></div>

                    <div className="p-1.5">
                      <a 
                        href="/api/auth/signout" 
                        className="flex items-center gap-2.5 px-3 py-2 text-rose-400 hover:bg-rose-500/10 rounded-lg transition-colors text-xs font-medium"
                      >
                        <span className="opacity-70">🚪</span> Logout
                      </a>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <Link href="/login" className="text-xs font-medium text-slate-400 hover:text-white transition-colors">
                  Log in
                </Link>
                <Link href="/register" className="text-xs font-bold bg-white text-slate-950 hover:bg-slate-200 px-3 py-1.5 rounded-lg transition-colors shadow-sm">
                  Register
                </Link>
              </div>
            )}
          </div>

        </div>
      </header>

      {/* Modernized Mobile Slide-out Drawer */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 flex justify-start lg:hidden">
          <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)}></div>
          <div className="relative w-72 max-w-[80%] bg-slate-950 border-r border-slate-800 h-full p-6 overflow-y-auto shadow-2xl flex flex-col">
            
            <div className="flex items-center justify-between border-b border-slate-800/60 pb-5 mb-5">
              <span className="font-mono font-bold text-slate-400 text-[10px] uppercase tracking-widest">~/menu</span>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="text-slate-500 hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* Main Links */}
            <nav className="space-y-1">
              <Link href="/" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-900 text-slate-300 hover:text-white font-medium text-sm transition-colors">
                <span className="opacity-60">🏠</span> Home Feed
              </Link>
              <Link href="/store" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-900 text-slate-300 hover:text-white font-medium text-sm transition-colors">
                <span className="opacity-60">🎁</span> Points Store
              </Link>
              <Link href="/premium" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-900 text-slate-300 hover:text-white font-medium text-sm transition-colors">
                <span className="opacity-60">⭐</span> Pro Memberships
              </Link>
              <Link href="/dashboard/hosting" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-900 text-slate-300 hover:text-white font-medium text-sm transition-colors">
                <span className="opacity-60">⚡</span> Hosting Dashboard
              </Link>
              <Link href="/bounties" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-900 text-slate-300 hover:text-white font-medium text-sm transition-colors">
                <span className="opacity-60">🎯</span> Bounties Directory
              </Link>

              {isAdmin && (
                <div className="pt-4 mt-2">
                  <Link 
                    href="/admin" 
                    onClick={() => setMobileMenuOpen(false)} 
                    className="flex items-center justify-center gap-2 w-full bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/20 font-bold px-4 py-2.5 rounded-lg text-xs transition-colors"
                  >
                    Admin Panel
                  </Link>
                </div>
              )}
            </nav>

            {/* Categories */}
            {categories.length > 0 && (
              <div className="mt-8">
                <h3 className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-widest px-3 mb-3">~/categories</h3>
                <div className="space-y-0.5">
                  {categories.map((cat: any) => (
                    <Link
                      key={cat.id}
                      href={`/category/${cat.id}`}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-900 text-sm font-medium text-slate-400 hover:text-slate-200 transition-colors"
                    >
                      <span className="truncate flex items-center gap-2">
                        <span className="opacity-50 text-indigo-400">#</span>
                        {cat.name}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}